import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  List,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message
} from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate, useParams } from 'react-router-dom'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { useAuthStore } from '../stores/authStore'
import { useVehicleStore } from '../stores/vehicleStore'
import type { Vehicle } from '../types'

const { Option } = Select
const { Text } = Typography

const LeaseTransferPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [messageApi, contextHolder] = message.useMessage()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [allReceipts, setAllReceipts] = useState<any[]>([])
  const [targetVehicle, setTargetVehicle] = useState<Vehicle | null>(null)

  const {
    vehicles,
    fetchVehicles,
    fetchStatistics,
    updateVehicle
  } = useVehicleStore()
  const { user, checkPermission } = useAuthStore()
  const canEdit = checkPermission('manage_vehicles')

  const vehicleId = String(id || '')

  const findVehicle = (list: Vehicle[]) =>
    list.find(v => [String(v._id || ''), String(v.id || ''), String(v.plate || '')].includes(vehicleId)) || null

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [receiptRes] = await Promise.all([
          simpleDirectCloudApi.getReceipts(),
          fetchVehicles()
        ])
        setAllReceipts(receiptRes.success && receiptRes.data ? receiptRes.data : [])
      } catch (err) {
        console.error(err)
        messageApi.error('加载换租数据失败')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [fetchVehicles])

  useEffect(() => {
    const vehicle = findVehicle(vehicles)
    setTargetVehicle(vehicle)
    if (!vehicle) return
    form.setFieldsValue({
      leaseCompany: vehicle.company,
      leaseContact: vehicle.contactPerson,
      leaseContact2: vehicle.contactPerson2,
      leasePhone: vehicle.phone,
      leasePhone2: vehicle.phone2,
      leaseStartDate: dayjs(),
      leaseEndDate: vehicle.dueDate ? dayjs(vehicle.dueDate) : null,
      leaseRentAmount: vehicle.rentAmount,
      leaseRentPeriod: 'yearly',
      leaseAddress: vehicle.address,
      leaseNotes: (vehicle as any).configuration || vehicle.notes
    })
  }, [vehicles, vehicleId, form])

  const historyReceiptsByLease = useMemo(() => {
    if (!targetVehicle) return [] as Array<{ lease: any; records: any[] }>
    const currentVehicleId = String(targetVehicle._id || targetVehicle.id || targetVehicle.plate || '')
    const history = Array.isArray((targetVehicle as any).leaseHistory) ? (targetVehicle as any).leaseHistory : []
    return history.map((lease: any) => {
      const leaseStart = lease?.startDate ? dayjs(lease.startDate) : null
      const leaseEnd = lease?.endDate ? dayjs(lease.endDate) : null
      const leaseCompany = String(lease?.company || '').trim()
      const records: any[] = []

      for (const receipt of allReceipts) {
        const items = Array.isArray(receipt?.items) ? receipt.items : []
        for (const it of items) {
          const itemVehicleId = String(it?.vehicleId || it?._id || it?.id || '')
          if (!itemVehicleId || itemVehicleId !== currentVehicleId) continue

          const chargeStartRaw = String(it?.chargeRangeStart || it?.startDate || receipt?.chargeRangeStart || receipt?.startDate || '')
          const chargeEndRaw = String(it?.chargeRangeEnd || it?.dueDate || receipt?.chargeRangeEnd || receipt?.dueDate || '')
          const chargeStart = chargeStartRaw ? dayjs(chargeStartRaw) : null
          const chargeEnd = chargeEndRaw ? dayjs(chargeEndRaw) : null
          const itemCompany = String(receipt?.company || receipt?.customerName || '').trim()

          let inLeaseRange = true
          if (leaseStart && leaseStart.isValid() && chargeEnd && chargeEnd.isValid() && chargeEnd.isBefore(leaseStart, 'day')) {
            inLeaseRange = false
          }
          if (leaseEnd && leaseEnd.isValid() && chargeStart && chargeStart.isValid() && chargeStart.isAfter(leaseEnd, 'day')) {
            inLeaseRange = false
          }
          const companyMatched = !!leaseCompany && !!itemCompany && leaseCompany === itemCompany
          if (!inLeaseRange && !companyMatched) continue

          records.push({
            receiptId: String(receipt?._id || receipt?.id || '-'),
            status: String(receipt?.status || it?.status || '-'),
            createdAt: String(receipt?.createdAt || ''),
            chargeRangeStart: chargeStartRaw,
            chargeRangeEnd: chargeEndRaw,
            amount: Number(it?.receivedAmount ?? it?.expectedAmount ?? receipt?.amount ?? 0),
            company: itemCompany || '-'
          })
        }
      }

      records.sort((a, b) => dayjs(b.chargeRangeEnd || b.createdAt).valueOf() - dayjs(a.chargeRangeEnd || a.createdAt).valueOf())
      return { lease, records }
    })
  }, [targetVehicle, allReceipts])

  const handleSubmit = async () => {
    if (!targetVehicle) return
    try {
      setSubmitting(true)
      const values = await form.validateFields()
      const vehicle = targetVehicle
      let leaseHistory = Array.isArray((vehicle as any).leaseHistory) ? [...(vehicle as any).leaseHistory] : []
      const pricingHistory = Array.isArray((vehicle as any).pricingHistory) ? [...(vehicle as any).pricingHistory] : []
      const newStart = values.leaseStartDate ? values.leaseStartDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
      const newEnd = values.leaseEndDate ? values.leaseEndDate.format('YYYY-MM-DD') : undefined
      const newLeaseId = `lease-${Date.now()}`

      if (!leaseHistory.length) {
        const baseLeaseId = vehicle.currentLeaseId || `lease-${Date.now()}-base`
        leaseHistory.push({
          leaseId: baseLeaseId,
          company: vehicle.company,
          contactPerson: vehicle.contactPerson,
          phone: vehicle.phone,
          contactPerson2: vehicle.contactPerson2,
          phone2: vehicle.phone2,
          startDate: vehicle.startDate || dayjs().format('YYYY-MM-DD'),
          rentAmount: vehicle.rentAmount,
          rentPeriod: vehicle.rentPeriod,
          rentPeriodMonths: (vehicle as any).rentPeriodMonths
        })
      }

      const currentLeaseId = vehicle.currentLeaseId || leaseHistory[leaseHistory.length - 1]?.leaseId
      leaseHistory = leaseHistory.map(l =>
        l.leaseId === currentLeaseId
          ? { ...l, endDate: dayjs(newStart).subtract(1, 'day').format('YYYY-MM-DD') }
          : l
      )

      leaseHistory.push({
        leaseId: newLeaseId,
        company: values.leaseCompany,
        contactPerson: values.leaseContact,
        phone: values.leasePhone,
        contactPerson2: values.leaseContact2,
        phone2: values.leasePhone2,
        startDate: newStart,
        endDate: newEnd,
        rentAmount: values.leaseRentAmount,
        rentPeriod: 'yearly',
        rentPeriodMonths: undefined
      })

      pricingHistory.push({
        startDate: newStart,
        rentAmount: Number(values.leaseRentAmount || vehicle.rentAmount || 0),
        rentPeriod: 'yearly',
        rentPeriodMonths: undefined
      })

      const payload = {
        currentLeaseId: newLeaseId,
        leaseHistory,
        pricingHistory,
        company: values.leaseCompany,
        contactPerson: values.leaseContact,
        contactPerson2: values.leaseContact2,
        phone: values.leasePhone,
        phone2: values.leasePhone2,
        rentAmount: values.leaseRentAmount,
        rentPeriod: 'yearly',
        rentPeriodMonths: undefined,
        dueDate: newEnd || vehicle.dueDate,
        address: values.leaseAddress || vehicle.address,
        configuration: values.leaseNotes || (vehicle as any).configuration
      }

      const oldRent = Number(vehicle.rentAmount || 0)
      const newRent = Number(values.leaseRentAmount || oldRent)
      if (Math.abs(newRent - oldRent) > 0.009) {
        const warnRes = await simpleDirectCloudApi.addRentAdjustRequest({
          vehicleId: vehicle._id || vehicle.id || vehicle.plate,
          plate: vehicle.plate,
          company: values.leaseCompany || vehicle.company,
          currentRent: oldRent,
          requestedRent: newRent,
          notes: '系统检测到换租时租金已修改，请总管理员核对是否有申请单。',
          status: 'pending',
          createdBy: user?.nickName || user?.id || 'system'
        })
        if (!warnRes.success) {
          messageApi.error(warnRes.error || '无法创建租金核对提醒，本次换租已阻止')
          return
        }
      }

      await updateVehicle(vehicle._id || vehicle.id, payload)
      await Promise.all([fetchVehicles(), fetchStatistics()])
      messageApi.success('换租成功')
      navigate('/vehicles')
    } catch (error: any) {
      messageApi.error(error?.message || '换租失败')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <Card loading />
  }

  if (!targetVehicle) {
    return (
      <Card>
        {contextHolder}
        <Space direction="vertical">
          <Text>未找到车辆，可能已被删除或编号无效。</Text>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/vehicles')}>返回车辆管理</Button>
        </Space>
      </Card>
    )
  }

  return (
    <div>
      {contextHolder}
      <Card
        title={`换租 - ${targetVehicle.plate}`}
        extra={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/vehicles')}>返回</Button>}
      >
        <Row gutter={16}>
          <Col xs={24} xl={13}>
            <Form form={form} layout="vertical" disabled={!canEdit}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="leaseCompany" label="新承租公司" rules={[{ required: true, message: '请输入新承租公司' }]}>
                    <Input placeholder="请输入新承租公司" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="leaseStartDate" label="新租期起租日" rules={[{ required: true, message: '请选择起租日' }]}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="leaseEndDate" label="新租期到期日">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="leaseContact" label="新联系人"><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="leasePhone" label="新联系电话"><Input /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="leaseContact2" label="新联系人2"><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="leasePhone2" label="新联系电话2"><Input /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="租金" name="leaseRentAmount" rules={[{ required: true, message: '请输入租金' }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="付款周期" required>
                    <Form.Item name="leaseRentPeriod" noStyle initialValue="yearly">
                      <Select style={{ width: '100%' }} disabled>
                        <Option value="yearly">年付</Option>
                      </Select>
                    </Form.Item>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="leaseAddress" label="地址"><Input /></Form.Item>
              <Form.Item name="leaseNotes" label="备注"><Input.TextArea rows={3} /></Form.Item>
              <Space>
                <Button onClick={() => navigate('/vehicles')}>取消</Button>
                <Button type="primary" onClick={handleSubmit} loading={submitting} disabled={!canEdit}>
                  确定换租
                </Button>
              </Space>
              {!canEdit && <div style={{ marginTop: 8, color: '#999' }}>当前账号无操作权限，仅可查看。</div>}
            </Form>
          </Col>
          <Col xs={24} xl={11}>
            <Divider orientation="left">历史租客</Divider>
            <List
              size="small"
              dataSource={historyReceiptsByLease}
              locale={{ emptyText: '暂无历史租客' }}
              renderItem={({ lease, records }: any) => (
                <List.Item>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <Text strong>{lease.company || '-'}</Text>
                      <Tag color="blue">{lease.startDate || '-'} ~ {lease.endDate || '至今'}</Tag>
                    </Space>
                    <Text type="secondary">租金：{lease.rentAmount ?? '-'} / {lease.rentPeriod || '月付'}</Text>
                    <Text>收款明细：{records.length ? `${records.length} 条` : '暂无'}</Text>
                    {records.length ? (
                      <Space direction="vertical" size={0}>
                        {records.map((r: any, idx: number) => (
                          <Text key={`${r.receiptId}-${idx}`} type="secondary">
                            {r.chargeRangeStart || '-'} ~ {r.chargeRangeEnd || '-'} | ￥{Number(r.amount || 0).toFixed(2)} | 单号 {r.receiptId}
                          </Text>
                        ))}
                      </Space>
                    ) : null}
                  </Space>
                </List.Item>
              )}
            />
          </Col>
        </Row>
      </Card>
    </div>
  )
}

export default LeaseTransferPage
